import type { EnticerSignal } from './EnticerSignals/types';
import type { JobCardDataFragment } from '../../graphQL/fragments/__generated__/jobCardData.gqlgenerated';
import type { StyleProp, ViewStyle } from 'react-native';

import { DislikeButton } from './ActionButtons/DislikeButton';
import { SaveButton } from './ActionButtons/SaveButton';
import { DefaultJobCard } from './DefaultJobCard';
import { useJobDislikeActions } from './useJobDislikeActions';
import { useLogFeatureDisplay } from '../../../homepage/logging/DisplayLog';
import { Pressable, type BoxRef } from '../../../mads';
import { useI18n } from '../../../shared/I18n';
import { getCachedViewJobHint } from '../../../shared/native-modules/tare/getCachedViewJobHint';
import { updateViewJobHint } from '../../../shared/native-modules/tare/viewJobHint';
import { setUniversalFocus } from '../../../shared/util/AccessibilityFocusManager';
import { isJobSponsored } from '../../../shared/util/urlUtils';
import { useSaveJobMutation } from '../../graphQL/mutations/__generated__/saveJobMutation.gqlgenerated';
import { useRunOnceAfterMount } from '../../util/useRunOnceAfterMount';

import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';

export type DislikedJobCardProps = {
    job: JobCardDataFragment;
    undoDislikeJob: () => void;
    onPressUpdateJobPreferences?: (job: JobCardDataFragment) => void;
    focusAfterRender?: boolean;
    onDismissDislikedJobCard?: () => void;
};

const isInitiallySavedJob = (job: JobCardDataFragment) => job.savedJob?.state === 'SAVED';

/**
 * JobCardData extends the GraphQL fragment with trackingKey.
 * Surfaces (homepage, ace, etc.) map their result's trackingKey to this field.
 */
export type JobCardData = JobCardDataFragment & {
    trackingKey?: string | null;
};

export type AppearanceMode = 'default' | 'featured';

export type JobCardHandle = {
    refreshFromCache: () => Promise<void>;
};

export type JobCardProps = {
    ref?: React.Ref<BoxRef>;
    handleRef?: React.Ref<JobCardHandle>;
    data: JobCardData;
    appearanceMode?: AppearanceMode;
    viewJobFrom?: string;
    dislikeJobPageId: string;
    loggedIn: boolean;
    containerStyle?: StyleProp<ViewStyle>;
    onJobCardImpression?: (job: JobCardData) => void;
    onPressJobCard?: (job: JobCardData, viewJobFrom?: string) => void;
    onPressSaveJobButton?: (job: JobCardData) => void;
    onPressDislikeJobButton?: () => void;
    onJobSaved?: (job: JobCardData, undo?: () => void) => void;
    onJobSaveError?: (job: JobCardData, error: unknown) => void;
    onPressUpdateJobPreferences?: (job: JobCardData) => void;
    onWillSaveOrUnsaveJob?: (job: JobCardData, willSave: boolean) => void;
    DislikedJobCard: React.ComponentType<DislikedJobCardProps>;
    clickEffectEnabled?: boolean;
    // show the easily apply badge when applicable
    showEasilyApplyBadge?: boolean;
    trackingKey?: string;
    showApplicationStatusBadge?: boolean;
    testID?: string;
    onDismissDislikedJobCard?: () => void;
    webTargetUrl?: string | null;
    showRelevanceSurvey?: boolean;
    /** Position/index of this job card within its section */
    position?: number;
    enticerSignals?: EnticerSignal[];
};

export const JobCard = forwardRef<BoxRef, JobCardProps>((jobCardProps, ref) => {
    const {
        handleRef,
        data: job,
        viewJobFrom,
        dislikeJobPageId,
        containerStyle,
        onJobCardImpression,
        onPressJobCard,
        onJobSaved,
        onJobSaveError,
        onPressUpdateJobPreferences,
        onWillSaveOrUnsaveJob,
        DislikedJobCard,
        trackingKey,
        testID,
        appearanceMode = 'default',
        onDismissDislikedJobCard,
    } = jobCardProps;

    const i18n = useI18n();
    const viewFullDetailsLabel = i18n.gettext('View full details of {0} at {1}', [
        job.title,
        job.sourceEmployerName,
    ]);

    const pressableRef = useRef<BoxRef>(null);
    const [isDislikeUndo, setIsDislikeUndo] = useState(false);

    const [isSavedJob, setIsSavedJob] = useState<boolean>(() => isInitiallySavedJob(job));

    useEffect(() => {
        setIsSavedJob(isInitiallySavedJob(job));
    }, [job, job.savedJob?.state]);

    const { isDislikedJob, dislikeJob, undoDislikeJob } = useJobDislikeActions(
        job.key,
        dislikeJobPageId,
        trackingKey,
    );

    // Refresh state from cache
    const refreshFromCache = useCallback(async () => {
        try {
            const cachedHint = await getCachedViewJobHint(job.key);
            if (cachedHint?.isSaved !== null && cachedHint?.isSaved !== undefined) {
                setIsSavedJob(cachedHint.isSaved);
            }
        } catch (error) {
            console.error('Error fetching cached view job hint:', error);
        }
    }, [job.key]);

    // Expose refreshFromCache via handleRef
    useImperativeHandle(
        handleRef,
        () => ({
            refreshFromCache,
        }),
        [refreshFromCache],
    );
    const [saveJobMutation, { loading: isSaveJobMutationLoading }] = useSaveJobMutation();
    const logFeatureDisplay = useLogFeatureDisplay();

    const handleUndoDislikeJob = useCallback(() => {
        undoDislikeJob();

        setIsDislikeUndo(true);
    }, [undoDislikeJob]);

    useLayoutEffect(() => {
        if (!isDislikeUndo) return;

        if (pressableRef.current) {
            setUniversalFocus(pressableRef, {
                setAccessibilityFocus: true,
                setKeyboardFocus: true,
                delay: 150,
            });
        }

        setIsDislikeUndo(false);
    }, [isDislikeUndo]);

    const saveOrUnsaveJob = async () => {
        const willSave = !isSavedJob;
        onWillSaveOrUnsaveJob?.(job, willSave);

        const updateJobState = async (state: 'VISITED' | 'SAVED'): Promise<boolean> => {
            try {
                const response = await saveJobMutation({
                    variables: { jobKey: job.key, state },
                });

                const success = !!response.data?.updateSavedJobState?.savedJob.job.key;
                if (success) {
                    const newIsSaved = state === 'SAVED';
                    await updateViewJobHint(job.key, { isSaved: newIsSaved });
                    setIsSavedJob(newIsSaved);
                }
                return success;
            } catch (err: unknown) {
                console.error(`Error while updating job state for jobKey - ${job.key}`, err);
                onJobSaveError?.(job, err);
                return false;
            }
        };

        const success = await updateJobState(willSave ? 'SAVED' : 'VISITED');

        // Only create undo function when saving job
        if (success && willSave) {
            const undoSaveJob = () => {
                void updateJobState('VISITED');
            };
            onJobSaved?.(job, undoSaveJob);
        }
    };

    useRunOnceAfterMount(() => {
        onJobCardImpression?.(job);
    });

    logFeatureDisplay({
        jobKey: job.key,
        field: 'sponsored',
        value: isJobSponsored(job.tracking?.jobClick?.url),
    });

    if (isDislikedJob) {
        return (
            <DislikedJobCard
                job={job}
                undoDislikeJob={handleUndoDislikeJob}
                onPressUpdateJobPreferences={onPressUpdateJobPreferences}
                focusAfterRender
                onDismissDislikedJobCard={onDismissDislikedJobCard}
            />
        );
    }

    const saveButton = (
        <SaveButton
            isSavedJob={isSavedJob}
            isSaveJobMutationLoading={isSaveJobMutationLoading}
            appearanceMode={appearanceMode}
            jobTitle={job.title}
            onPressSave={() => {
                jobCardProps.onPressSaveJobButton?.(job);
                // eslint-disable-next-line @typescript-eslint/no-floating-promises -- FIXME
                saveOrUnsaveJob();
            }}
            testID={
                testID
                    ? isSavedJob
                        ? `${testID}-unsaveJobButton`
                        : `${testID}-saveJobButton`
                    : undefined
            }
        />
    );

    const dislikeButton = (
        <DislikeButton
            jobTitle={job.title}
            testID={testID ? `${testID}-dislikeJobButton` : 'dislikeJobButton'}
            onPressDislike={() => {
                jobCardProps.onPressDislikeJobButton?.();
                dislikeJob();
                // if saved, undo that
                if (isSavedJob) {
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- FIXME
                    saveOrUnsaveJob();
                }
            }}
        />
    );

    const handlePressJobCard = () => {
        onPressJobCard?.(job, viewJobFrom);
    };

    const defaultJobCard = ({ pressed }: { pressed: boolean }) => (
        <DefaultJobCard
            pressed={pressed}
            saveButton={saveButton}
            dislikeButton={dislikeButton}
            jobCardProps={jobCardProps}
            viewFullDetailsLabel={viewFullDetailsLabel}
            onPressJobCard={handlePressJobCard}
            trackingKey={trackingKey}
            showApplicationStatusBadge={jobCardProps.showApplicationStatusBadge}
        />
    );

    const jobCards: Record<AppearanceMode, (props: { pressed: boolean }) => React.ReactNode> = {
        default: defaultJobCard,
        featured: defaultJobCard,
    } as const;

    return (
        <Pressable
            ref={(r) => {
                pressableRef.current = r;
                if (typeof ref === 'function') {
                    ref(r);
                } else if (ref) {
                    ref.current = r;
                }
            }}
            sx={containerStyle}
            // Do not treat this wrapper as one accessibility element; descendants (details action,
            // job-actions toolbar, save/dislike) are exposed to assistive tech instead.
            accessible={false}
            // Do not make this outer Pressable a separate keyboard/TalkBack focus target on Android.
            focusable={false}
            onPress={handlePressJobCard}
            testID={testID ?? `job-card-${job.key}`}
        >
            {({ pressed }) => jobCards[appearanceMode]({ pressed })}
        </Pressable>
    );
});
